<template>
  <div class="w-full flex flex-col">
    <div class="mb-2.5">
      <p class="text-sm text-[#58595b] mb-2.5 text-center">
        PSI = Bar * 14.504 <br />
        Bar = PSI / 14.504
      </p>
      <div class="font-bold text-center">Fill in one value to calculate the other</div>
    </div>
    <div class="w-full flex flex-col gap-1">
      <div class="input-block w-full flex flex-col gap-0.5">
        <div class="w-full text-sm text-gray">PSI</div>
        <label
          :for="`${calcType}-${CalculatorFieldsTypes.psi}-input`"
          class="w-full">
          <input
            type="number"
            :id="`${calcType}-${CalculatorFieldsTypes.psi}-input`"
            :title="`${calcType}-${CalculatorFieldsTypes.psi}-input`"
            placeholder=""
            class="w-full h-8 border border-black text-sm text-gray py-2 px-4 rounded-full"
            :class="{ 'border-blue-600 bg-yellow-100': state.inputFields.includes(CalculatorFieldsTypes.psi) }"
            v-model="state.psi"
            @input="updateInputAndOutputFields(CalculatorFieldsTypes.psi, state.psi)" />
        </label>
      </div>
      <div class="input-block w-full flex flex-col gap-0.5">
        <div class="w-full text-sm text-gray">Bar</div>
        <label
          :for="`${calcType}-${CalculatorFieldsTypes.bar}-input`"
          class="w-full">
          <input
            type="number"
            :id="`${calcType}-${CalculatorFieldsTypes.bar}-input`"
            :title="`${calcType}-${CalculatorFieldsTypes.bar}-input`"
            placeholder=""
            class="w-full h-8 border border-black text-sm text-gray py-2 px-4 rounded-full"
            :class="{ 'border-blue-600 bg-yellow-100': state.inputFields.includes(CalculatorFieldsTypes.bar) }"
            v-model="state.bar"
            @input="updateInputAndOutputFields(CalculatorFieldsTypes.bar, state.bar)" />
        </label>
      </div>
      <div class="w-full flex items-center justify-center px-4 gap-2.5 mt-1">
        <div
          class="text-sm py-1.5 px-3 rounded text-white bg-base-color hover:bg-dark-base-color cursor-pointer flex items-center justify-center transition-colors duration-300"
          :class="{ 'grayscale pointer-events-none': state.inputFields.length === 0 }"
          @click="resetAction">
          Reset
        </div>
        <div
          class="w-full max-w-44 text-sm py-1.5 px-3 rounded text-white bg-base-color hover:bg-dark-base-color cursor-pointer flex items-center justify-center transition-colors duration-300"
          :class="{ 'grayscale pointer-events-none': state.inputFields.length < minInputFieldsQty }"
          @click="calculateAction">
          Calculate
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, reactive } from 'vue';
import CalculatorsHelper from '@/helpers/global/calculators.helper';
import GlobalHelper from '@/helpers/global/global.helper';

enum CalculatorFieldsTypes {
  psi = 'psi',
  bar = 'bar',
}

const state = reactive({
  psi: '' as string | number,
  bar: '' as string | number,
  inputFields: [] as string[],
  outputFields: [] as string[],
});

const calculatorFieldsList: CalculatorFieldsTypes[] = [CalculatorFieldsTypes.psi, CalculatorFieldsTypes.bar];
const calcType: string = 'pressure-conversion-calculator';
const minInputFieldsQty: number = 1;

const updateInputAndOutputFields = (field: CalculatorFieldsTypes, fieldValue: string | number) => {
  CalculatorsHelper.generateInputAndOutputFields(state.inputFields, state.outputFields, field, fieldValue, calcType, minInputFieldsQty);
  state.inputFields = CalculatorsHelper.getInputFields();
  state.outputFields = CalculatorsHelper.getOutputFields();
};

const setOutputFields = () => {
  calculatorFieldsList.forEach((field) => {
    state.outputFields.push(field);
  });
};

const resetAction = () => {
  state.psi = '';
  state.bar = '';
  state.inputFields = [];
  state.outputFields = [];

  setOutputFields();
};

const calculateAction = () => {
  if (state.inputFields.length === minInputFieldsQty) {
    const psiValue = Number(state.psi);
    const barValue = Number(state.bar);

    const firstOutputField = state.outputFields[0];

    if (firstOutputField === CalculatorFieldsTypes.psi) {
      state.psi = GlobalHelper.roundToDecimals(barValue * 14.503773773, 4);
    } else {
      state.bar = GlobalHelper.roundToDecimals(psiValue / 14.503773773, 4);
    }
  }
};

onMounted(() => {
  setOutputFields();
});
</script>
